import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  fetchOverview,
  fetchSalesChart,
  fetchOrdersChart,
  fetchRevenueByStore,
} from "../store/actions/analyticsActions";
import { fetchStores } from "../store/actions/storeActions";
import { Card } from "../components/ui/Card";
import {
  TrendingUp,
  Users,
  ShoppingCart,
  Package,
  DollarSign,
  Percent,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { formatCurrency } from "../utils/formatCurrency";
import * as ratesService from "../services/ratesService";

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.text.body};
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 16px 0 24px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.border || "#e5e7eb"};
`;

const Tab = styled.button`
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  background: none;
  color: ${({ active, theme }) => (active ? (theme.colors?.primary || "#6366f1") : theme.colors?.text?.body || "#6b7280")};
  border-bottom: 2px solid ${({ active }) => (active ? "currentColor" : "transparent")};
  margin-bottom: -1px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors?.text?.heading || "#111827"};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  @media (min-width: ${({ theme }) => theme.breakpoints?.md || 768}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: ${({ theme }) => theme.breakpoints?.lg || 1024}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const TABS = { OVERVIEW: "overview", SALES: "sales", ORDERS: "orders", STORES: "stores", DISCOUNTS: "discounts" };

const periodToDays = (p) => (p === "7d" ? 7 : p === "90d" ? 90 : 30);

const Analytics = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { overview, salesChart, ordersChart, revenueByStore, loading } = useSelector((state) => state.analytics);
  const stores = useSelector((state) => state.stores?.stores ?? []);
  const user = useSelector((state) => state.auth?.user);

  const [period, setPeriod] = useState("30d");
  const [currency, setCurrency] = useState(() => user?.currency || "USD");
  const [rates, setRates] = useState(null);
  const [storeId, setStoreId] = useState("");
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);

  const params = { period, storeId: storeId || undefined };
  const days = periodToDays(period);
  const chartParams = { days, storeId: storeId || undefined };

  useEffect(() => {
    if (user?.currency) setCurrency(user.currency);
  }, [user?.currency]);

  useEffect(() => {
    ratesService.getRates().then((r) => setRates(r.rates)).catch(() => setRates(null));
  }, []);

  useEffect(() => {
    dispatch(fetchStores());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchOverview(params));
  }, [dispatch, period, storeId]);

  useEffect(() => {
    dispatch(fetchSalesChart(chartParams));
    dispatch(fetchOrdersChart(chartParams));
  }, [dispatch, days, storeId]);

  useEffect(() => {
    if (activeTab === TABS.STORES) {
      dispatch(fetchRevenueByStore({ period }));
    }
  }, [dispatch, period, activeTab]);

  const tabNameKeys = { OVERVIEW: "analytics.overview", SALES: "analytics.sales", ORDERS: "analytics.orders", STORES: "analytics.stores", DISCOUNTS: "analytics.discounts" };
  const baseCurrency = "USD";
  const fmt = (v, from = baseCurrency) => {
    if (v == null) return "—";
    const converted = rates ? ratesService.convert(v, from, currency, rates) : v;
    return formatCurrency(converted, currency);
  };
  const overviewStats = [
    { nameKey: "analytics.totalRevenue", key: "totalRevenue", icon: DollarSign, format: (v) => fmt(v) },
    { nameKey: "analytics.netRevenue", key: "netRevenue", icon: TrendingUp, format: (v) => fmt(v) },
    { nameKey: "analytics.totalOrders", key: "totalOrders", icon: ShoppingCart, format: (v) => (v == null ? "—" : String(v)) },
    { nameKey: "analytics.customers", key: "totalCustomers", icon: Users, format: (v) => (v == null ? "—" : String(v)) },
    { nameKey: "analytics.products", key: "totalProducts", icon: Package, format: (v) => (v == null ? "—" : String(v)) },
    { nameKey: "analytics.aov", key: "averageOrderValue", icon: DollarSign, format: (v) => fmt(v) },
    { nameKey: "analytics.newCustomersPeriod", key: "newCustomersInPeriod", icon: Users, format: (v) => (v == null ? "—" : String(v)) },
  ];

  const salesData = (salesChart || []).map((d) => ({
    name: d.date,
    value: rates ? ratesService.convert(d.total || 0, baseCurrency, currency, rates) : (d.total || 0),
    count: d.count,
  }));
  const ordersChartData = (ordersChart || []).map((d) => ({
    name: d.date,
    completed: d.completed || 0,
    cancelled: d.cancelled || 0,
    total: (d.completed || 0) + (d.cancelled || 0),
  }));

  const totalRevenue = overview?.totalRevenue ?? 0;
  const totalDiscount = overview?.totalDiscount ?? 0;
  const discountPct = totalRevenue > 0 ? ((totalDiscount / totalRevenue) * 100).toFixed(1) : "0";

  return (
    <div className="animate-fadeIn">
      <div style={{ marginBottom: 8 }}>
        <Title>{t("analytics.title")}</Title>
        <Sub>{t("analytics.subtitle")}</Sub>
      </div>

      <FiltersRow>
        <span style={{ fontWeight: 500, marginRight: 4 }}>{t("analytics.period")}</span>
        {["7d", "30d", "90d"].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: period === p ? "2px solid #6366f1" : "1px solid #e5e7eb",
              background: period === p ? "#eef2ff" : "#fff",
              cursor: "pointer",
              fontWeight: period === p ? 600 : 400,
            }}
          >
            {p === "7d" ? t("analytics.days7") : p === "30d" ? t("analytics.days30") : t("analytics.days90")}
          </button>
        ))}
        <span style={{ marginLeft: 16, fontWeight: 500 }}>{t("analytics.store")}</span>
        <select
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 6, minWidth: 180 }}
        >
          <option value="">{t("analytics.allStores")}</option>
          {(stores || []).map((s) => (
            <option key={s._id} value={s._id}>
              {s.name || s.storeName || "Store"}
            </option>
          ))}
        </select>
        <span style={{ marginLeft: 16, fontWeight: 500 }}>{t("analytics.currency", "Currency")}</span>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ padding: "8px 12px", borderRadius: 6 }}>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
        </select>
      </FiltersRow>

      <TabRow>
        {Object.entries(TABS).map(([label, key]) => (
          <Tab key={key} active={activeTab === key} onClick={() => setActiveTab(key)}>
            {t(tabNameKeys[key])}
          </Tab>
        ))}
      </TabRow>

      {activeTab === TABS.OVERVIEW && (
        <>
          <StatsGrid>
            {overviewStats.map((s) => {
              const value = overview != null && s.key in overview ? overview[s.key] : null;
              const display = loading && overview == null ? "…" : s.format(value);
              return (
                <Card key={s.nameKey} style={{ padding: "24px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <p style={{ fontSize: "14px", fontWeight: 500, color: "#6b7280", margin: 0 }}>{t(s.nameKey)}</p>
                      <p style={{ margin: "8px 0 0", fontSize: "24px", fontWeight: 600, color: "#111827" }}>{display}</p>
                    </div>
                    <div style={{ borderRadius: "9999px", padding: "12px", background: "rgba(26, 31, 44, 0.1)" }}>
                      <s.icon style={{ height: "24px", width: "24px", color: "#1A1F2C" }} />
                    </div>
                  </div>
                </Card>
              );
            })}
          </StatsGrid>
        </>
      )}

      {activeTab === TABS.SALES && (
        <Card style={{ padding: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("analytics.revenueOverTime")}</h2>
          <div style={{ height: "320px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => (v >= 1000 ? v / 1000 + "k" : v)} />
                <Tooltip formatter={(v) => [formatCurrency(v, currency), t("analytics.revenue")]} />
                <Line type="monotone" dataKey="value" name={t("analytics.revenue")} stroke="#8b5cf6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {activeTab === TABS.ORDERS && (
        <Card style={{ padding: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("analytics.ordersOverTime")}</h2>
          <div style={{ height: "320px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ordersChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" name={t("analytics.completed")} fill="#22c55e" stackId="a" />
                <Bar dataKey="cancelled" name={t("analytics.cancelled")} fill="#ef4444" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {activeTab === TABS.STORES && (
        <Card style={{ padding: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("analytics.revenueByStore")}</h2>
          {loading && revenueByStore.length === 0 ? (
            <p style={{ color: "#6b7280" }}>{t("analytics.loading")}</p>
          ) : revenueByStore.length === 0 ? (
            <p style={{ color: "#6b7280" }}>{t("analytics.noDataForPeriod")}</p>
          ) : (
            <>
              <div style={{ height: "320px", marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueByStore} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => "$" + (v >= 1000 ? v / 1000 + "k" : v)} />
                    <YAxis type="category" dataKey="storeName" width={80} />
                    <Tooltip formatter={(v) => [formatCurrency(v, currency), t("analytics.revenue")]} />
                    <Bar dataKey="revenue" name={t("analytics.revenue")} fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ textAlign: "left", padding: "8px 0" }}>{t("analytics.storeLabel")}</th>
                    <th style={{ textAlign: "right", padding: "8px 0" }}>{t("analytics.revenue")}</th>
                    <th style={{ textAlign: "right", padding: "8px 0" }}>{t("analytics.orders")}</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueByStore.map((r) => (
                    <tr key={r.storeId} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 0" }}>{r.storeName}</td>
                      <td style={{ textAlign: "right", padding: "8px 0" }}>{fmt(r.revenue)}</td>
                      <td style={{ textAlign: "right", padding: "8px 0" }}>{r.orders ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Card>
      )}

      {activeTab === TABS.DISCOUNTS && (
        <Card style={{ padding: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>{t("analytics.discounts")}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ borderRadius: "9999px", padding: "12px", background: "rgba(26, 31, 44, 0.1)" }}>
                <Percent style={{ height: "24px", width: "24px", color: "#1A1F2C" }} />
              </div>
              <div>
                <p style={{ fontSize: "14px", fontWeight: 500, color: "#6b7280", margin: 0 }}>{t("analytics.totalDiscountPeriod")}</p>
                <p style={{ margin: "4px 0 0", fontSize: "24px", fontWeight: 600, color: "#111827" }}>
                  {loading && overview == null ? "…" : fmt(overview?.totalDiscount)}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ borderRadius: "9999px", padding: "12px", background: "rgba(26, 31, 44, 0.1)" }}>
                <DollarSign style={{ height: "24px", width: "24px", color: "#1A1F2C" }} />
              </div>
              <div>
                <p style={{ fontSize: "14px", fontWeight: 500, color: "#6b7280", margin: 0 }}>{t("analytics.discountPctOfRevenue")}</p>
                <p style={{ margin: "4px 0 0", fontSize: "24px", fontWeight: 600, color: "#111827" }}>
                  {loading && overview == null ? "…" : discountPct + "%"}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
